import {useContext, useEffect, useRef, useState} from 'react';

import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Checkbox, FormControlLabel, Grid, TextField, Typography} from '@mui/material';

import {
  CreateExternalApplicantResponseBody,
  FilterOptions,
  ScreeningApplicant,
} from '../types/screeningExternalApplicantsTypes';

import {InviteToScreening} from './InviteToScreening';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';
import {SimpleFormikForm} from '../../../../components/SimpleFormikForm/SimpleFormikForm';
import {FieldType} from '../../../../components/SimpleFormikForm/types';
import {CompanyContext} from '../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';
import {Patch} from '../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../lib/api/PATCH/PatchRoutes';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {CreateOrUpdateExternalApplicantRequestBody} from '../../../../lib/api/RequestBodies/features/company/externalApplicants/ExternalApplicantRequestBodies';
import {CompanyAdminRoleData} from '../../../../types/User';
import {validateEmail} from '../../../../utils/validators';

interface Props {
  open: boolean;
  onClose: () => void;
  data?: ScreeningApplicant;
  refetch: () => void;
  refetchFilters: () => void;
  filterOptions?: FilterOptions;
}

interface Option {
  label: string;
  value: string;
}

interface FormValues {
  location: Option;
  jobPosting: Option;
  companyDepartment: Option | null;
  firstName: string;
  lastName?: string;
  email: string;
  sendViaEmail: boolean;
}

const Header = ({label}: {label: string}) => (
  <Typography variant='h4' marginBottom='4px' marginTop='8px'>
    {label}
  </Typography>
);

export const InviteApplicantModal = ({open, onClose, data, refetch, refetchFilters, filterOptions}: Props) => {
  const isEditMode = !!data;

  const {t: tr} = useTranslation('translation', {keyPrefix: 'screeningModule.inviteApplicantModal'});
  const {t: ts} = useTranslation('translation', {keyPrefix: 'shared'});
  const {dispatchMessage} = useContext(SnackBarContext);

  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [surveyUrl, setSurveyUrl] = useState<string>('');
  const shouldSendViaEmailRef = useRef(false);
  const {currentUser} = useContext(UserContext);
  const currentLanguage = useContext(LanguageContext);
  const {productConfiguration} = useContext(CompanyContext);

  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId || '';

  useEffect(() => {
    if (isEditMode && data) {
      setSurveyUrl(data.talentSurvey?.surveyUrl);
    }
  }, [data, isEditMode]);

  const getTitle = () => {
    if (formSubmitted) {
      return tr('inviteToScreeningTitle');
    }
    return isEditMode ? tr('editTitle') : tr('title');
  };

  const createOptions = (options: string[]) => {
    return options.map((option) => ({label: option, value: option}));
  };

  const createDepartmentOptions = () => {
    return (filterOptions?.departments || []).map((department) => ({
      label: department.name[currentLanguage],
      value: department.resourceId,
    }));
  };

  const fields: FieldType<keyof FormValues>[] = [
    {
      componentType: 'autocomplete',
      fieldName: 'companyDepartment',
      fieldHeader: <Header label={tr('departmentLabel')} />,
      testId: 'department-add-applicant-field',
      options: createDepartmentOptions(),
      freeSolo: false,
      disableClearable: false,
      forcePopupIcon: (filterOptions?.departments || []).length > 0,
      placeholder: tr('departmentPlaceholder') || '',
    },
    {
      componentType: 'autocomplete',
      fieldName: 'jobPosting',
      fieldHeader: <Header label={tr('jobPostingLabel')} />,
      testId: 'job-posting-add-applicant-field',
      options: createOptions(filterOptions?.jobPostings || []),
      renderInput: (params) => <TextField {...params} />,
      freeSolo: true,
      forcePopupIcon: (filterOptions?.jobPostings || []).length > 0,
      placeholder: filterOptions?.jobPostings?.length
        ? tr('jobPostingPlaceholder') || ''
        : tr('createJobPosting') || '',
    },
    {
      componentType: 'autocomplete',
      fieldName: 'location',
      fieldHeader: <Header label={tr('locationLabel')} />,
      testId: 'location-add-applicant-field',
      options: createOptions(filterOptions?.locations || []),
      renderInput: (params) => <TextField {...params} />,
      freeSolo: true,
      forcePopupIcon: (filterOptions?.locations || []).length > 0,
      placeholder: filterOptions?.locations?.length ? tr('locationPlaceholder') || '' : tr('createLocation') || '',
    },
    {
      componentType: 'text-field',
      fieldName: 'firstName',
      placeholder: tr('applicantPlaceholderFirstName') || 'Enter applicant name',
      fieldHeader: <Header label={tr('applicantLabelFirstName')} />,
      type: 'text',
      testId: 'applicant-first-name-add-applicant-field',
    },
    {
      componentType: 'text-field',
      fieldName: 'lastName',
      placeholder: tr('applicantPlaceholderLastName') || 'Enter applicant name',
      fieldHeader: <Header label={tr('applicantLabelLastName')} />,
      type: 'text',
      testId: 'applicant-last-name-add-applicant-field',
    },
    {
      componentType: 'text-field',
      fieldName: 'email',
      placeholder: tr('emailPlaceholder') || '',
      fieldHeader: <Header label={tr('emailLabel')} />,
      type: 'email',
      autoComplete: 'email',
      testId: 'applicant-email-add-applicant-field',
    },
  ];

  const {makeRequest: createApplicant, isLoading: isCreating} = useApiWrite<
    Post.CreateExternalApplicant,
    CreateExternalApplicantResponseBody
  >({
    method: 'post',
    path: PostRoutes[Post.CreateExternalApplicant](companyId),
    onSuccess: (data) => {
      if (shouldSendViaEmailRef.current) {
        onCloseModal();
      } else {
        setFormSubmitted(true);
        setSurveyUrl(data.talentSurvey.surveyUrl);
      }
      refetch();
      refetchFilters();
      productConfiguration.invalidateCache();
    },
    onError: () => onError('create'),
  });

  const {makeRequest: updateApplicant, isLoading: isUpdating} = useApiWrite<
    Patch.UpdateScreeningApplicant,
    CreateExternalApplicantResponseBody
  >({
    method: 'patch',
    path: PatchRoutes[Patch.UpdateScreeningApplicant](companyId, data?.resourceId || ''),
    onSuccess: (data) => {
      if (shouldSendViaEmailRef.current) {
        onCloseModal();
      } else {
        setFormSubmitted(true);
        setSurveyUrl(data.talentSurvey.surveyUrl);
      }
      refetch();
      refetchFilters();
    },
    onError: () => onError('edit'),
  });

  const buildInvitePayload = (
    formattedValues: FormValues,
    mode: 'create' | 'update'
  ): CreateOrUpdateExternalApplicantRequestBody => {
    const emailTrimmed = formattedValues.email?.trim() ?? '';
    const payload: CreateOrUpdateExternalApplicantRequestBody = {
      firstName: formattedValues.firstName,
      lastName: formattedValues.lastName,
      location: formattedValues.location.value,
      jobPosting: formattedValues.jobPosting.value,
      sendViaEmail: formattedValues.sendViaEmail,
      ...(emailTrimmed ? {email: emailTrimmed} : {}),
    };

    const deptId = formattedValues.companyDepartment?.value || null;
    if (mode === 'create') {
      if (deptId) {
        payload.companyDepartmentId = deptId;
      }
    } else {
      payload.companyDepartmentId = deptId;
    }

    return payload;
  };

  const onSubmit = (values: FormValues) => {
    const formattedValues = {
      ...values,
      location:
        typeof values.location === 'string' ? {label: values.location, value: values.location} : values.location,
      jobPosting:
        typeof values.jobPosting === 'string'
          ? {label: values.jobPosting, value: values.jobPosting}
          : values.jobPosting,
    };
    shouldSendViaEmailRef.current = formattedValues.sendViaEmail;

    if (!isEditMode) {
      createApplicant(buildInvitePayload(formattedValues, 'create'));
    } else {
      updateApplicant(buildInvitePayload(formattedValues, 'update'));
    }
  };

  const departmentOptions = createDepartmentOptions();

  const initialFormValues: FormValues = {
    location: data?.location ? {label: data.location, value: data.location} : {label: '', value: ''},
    jobPosting: data?.jobPosting ? {label: data.jobPosting, value: data.jobPosting} : {label: '', value: ''},
    companyDepartment: data?.companyDepartmentId
      ? departmentOptions.find((department) => department.value === data.companyDepartmentId) || {
        label: data.companyDepartmentId,
        value: data.companyDepartmentId,
      }
      : null,
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    email: data?.email ?? '',
    sendViaEmail: data?.sendViaEmail === true,
  };

  const validate = (values: FormValues) => {
    const errors: Record<string, string> = {};

    if (values.jobPosting.value === '') {
      errors.jobPosting =
        ts('formValidation.required', {fieldName: tr('jobPostingLabel')}) || 'Job posting is required';
    }
    if (values.location.value === '') {
      errors.location = ts('formValidation.required', {fieldName: tr('locationLabel')}) || 'Location is required';
    }
    if (!values.firstName) {
      errors.firstName = ts('formValidation.required', {fieldName: tr('applicantLabelFirstName')});
    }

    const emailTrimmed = values.email?.trim() ?? '';
    if (values.sendViaEmail) {
      if (!emailTrimmed) {
        errors.email = ts('formValidation.required', {fieldName: tr('emailLabel')}) || 'Email is required';
      } else if (!validateEmail(values.email?.trim())) {
        errors.email = ts('formValidation.email') || 'Invalid email';
      }
    } else if (emailTrimmed && !validateEmail(values.email?.trim())) {
      errors.email = ts('formValidation.email') || 'Invalid email';
    }

    return errors;
  };

  const submitExtraDisabled = (v: FormValues) => {
    if (!v.sendViaEmail) return false;
    return !validateEmail(v.email?.trim());
  };

  const renderSendViaEmailRow = (formik: FormikProps<FormValues>) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={formik.values.sendViaEmail}
          disabled={isCreating || isUpdating}
          onChange={(e) => formik.setFieldValue('sendViaEmail', e.target.checked)}
          sx={{
            color: greenColors[700],
            '&.Mui-checked': {color: orangeColors[3]},
          }}
        />
      }
      label={<Typography variant='body2'>{tr('sendViaEmailLabel')}</Typography>}
    />
  );

  const onCloseModal = () => {
    onClose();
    setFormSubmitted(false);
  };

  const onError = (type: 'create' | 'edit') => {
    if (type === 'create') {
      dispatchMessage(ts('formSubmit.creationError'), 'error');
    } else {
      dispatchMessage(ts('formSubmit.error'), 'error');
    }
  };

  return (
    <EmpionDialog
      open={open}
      onClose={onCloseModal}
      title={getTitle()}
      showCloseButton
      backgroundColor='#fff'
      sxTitle={{
        fontSize: '28px',
        fontWeight: 700,
        lineHeight: '42px',
      }}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '736px',
            padding: '24px',
            borderRadius: '16px',
          },
        },
      }}
    >
      {isEditMode && (
        <Grid item xs={12}>
          <Typography
            variant='h3'
            sx={{
              color: greenColors[700],
              fontWeight: 400,
            }}
          >
            {tr('subtitleEditNote')}
          </Typography>
        </Grid>
      )}
      {!formSubmitted ? (
        <Box marginTop={2}>
          <SimpleFormikForm
            initialValues={initialFormValues}
            fields={fields}
            validate={validate}
            onSubmit={onSubmit}
            buttonText={ts('formButtons.confirm') || 'Confirm'}
            buttonFullWidth={false}
            buttonPosition='flex-end'
            isFormDisabled={isCreating || isUpdating}
            renderBeforeSubmit={renderSendViaEmailRow}
            submitExtraDisabled={submitExtraDisabled}
          />
        </Box>
      ) : (
        <InviteToScreening surveyUrl={surveyUrl} />
      )}
    </EmpionDialog>
  );
};
